// Resolves a stored ArcGIS service layer into the concrete per-sublayer feature URLs the map
// should build. Both FeatureServer and (queryable) MapServer services are supported —
// L.esri.featureLayer consumes layer endpoints of either kind through the same /query API.
//
//   .../FeatureServer/{n} or .../MapServer/{n}  -> [the URL itself]  (single sublayer; `layers` ignored)
//   .../FeatureServer or .../MapServer          -> [.../{id}, …]     (one per selected id in the `layers` CSV)
//   anything else                               -> []                (skip)
//
// `layers` is the CSV stored in MapLayer.Layers (e.g. "0,1"); whitespace is tolerated and
// non-numeric tokens are dropped. A root URL with no usable id resolves to [].

function basePath(url) {
	return url.split("?")[0].split("#")[0].replace(/\/+$/, "");
}

function isAllDigits(token) {
	return token.length > 0 && /^[0-9]+$/.test(token);
}

// Numeric sublayer ids from the stored MapLayer.Layers CSV; whitespace tolerated,
// non-numeric tokens dropped.
export function parseSublayerIds(layers) {
	if (typeof layers !== "string") {
		return [];
	}
	return layers
		.split(",")
		.map((token) => token.trim())
		.filter(isAllDigits)
		.map((token) => parseInt(token, 10));
}

const SERVICE_ROOT_RE = /\/(?:FeatureServer|MapServer)$/i;
const SUBLAYER_RE = /^(.*\/(?:FeatureServer|MapServer))\/(\d+)$/i;
// REST resource pages users copy from the ArcGIS services directory HTML (.../MapServer/layers,
// .../FeatureServer/0/query, ...). One such trailing segment is stripped, but only when what
// remains is a recognizable root or sublayer — never for arbitrary URLs.
const TRAILING_RESOURCE_RE = /\/(?:query|layers|legend|info)$/i;

function classifyBasePath(base) {
	if (SERVICE_ROOT_RE.test(base)) {
		return { base: base, isRoot: true, sublayerId: null };
	}
	const sublayerMatch = base.match(SUBLAYER_RE);
	if (sublayerMatch) {
		return { base: sublayerMatch[1], isRoot: false, sublayerId: sublayerMatch[2] };
	}
	return null;
}

// Classifies a typed/stored ArcGIS URL against the service shape rule the admin form uses:
//
//   .../FeatureServer or .../MapServer          -> { base: <cleaned root>, isRoot: true,  sublayerId: null }
//   .../FeatureServer/{n} or .../MapServer/{n}  -> { base: <the root>,     isRoot: false, sublayerId: "{n}" }
//   anything else                               -> { base: <cleaned path>, isRoot: false, sublayerId: null }
//
// `base` always has query/hash and trailing slashes stripped, so a sublayer match canonicalizes to
// its service root (the admin picker rewrites the URL field with it and pre-selects `sublayerId`).
// A trailing REST resource segment (/query, /layers, /legend, /info) is stripped once when doing so
// yields a recognized shape, so URLs copied from the REST directory's HTML pages classify too.
// This is deliberately stricter than resolveArcgisSublayerUrls' any-"/{n}" sublayer rule: the picker
// only rewrites URLs it positively recognizes. The C# mirror (MapLayerFormHelper.IsArcGisServiceRoot)
// must stay independent — server validation can't depend on client script — and is kept in sync
// with the root rule here by convention.
export function arcgisUrlShape(url) {
	if (typeof url !== "string" || url.trim() === "") {
		return { base: "", isRoot: false, sublayerId: null };
	}
	const base = basePath(url.trim());
	const direct = classifyBasePath(base);
	if (direct !== null) {
		return direct;
	}
	const stripped = base.replace(TRAILING_RESOURCE_RE, "");
	if (stripped !== base) {
		const retried = classifyBasePath(stripped);
		if (retried !== null) {
			return retried;
		}
	}
	return { base: base, isRoot: false, sublayerId: null };
}

export function resolveArcgisSublayerUrls(url, layers) {
	if (typeof url !== "string" || url.trim() === "") {
		return [];
	}

	const base = basePath(url);

	// Specific sublayer: path ends with a numeric id.
	if (/\/\d+$/.test(base)) {
		return [url];
	}

	// Service root: path ends with "FeatureServer" or "MapServer" (case-insensitive).
	if (/\/(?:FeatureServer|MapServer)$/i.test(base)) {
		return parseSublayerIds(layers).map((id) => `${base}/${id}`);
	}

	return [];
}

// Classifies the ?f=json document the admin picker fetched, so the form reacts to what the URL
// actually returned instead of guessing from its shape. ArcGIS answers HTTP 200 for everything:
// errors ({error}), folder listings ({services}), single-layer documents, and service roots.
//
//   { kind: "serviceRoot", layers: [...], supportsQuery: true|false|null, supportsExport: true|false|null, title: <string|null> }
//   { kind: "singleLayer", id: <number>, name: <string>, geometryType: <string|null> }
//   { kind: "directory" }
//   { kind: "arcgisError", message: <string> }
//   { kind: "unrecognized" }
//
// serviceRoot.layers keeps only selectable feature layers: group layers (non-empty subLayerIds)
// are dropped, and a layer that declares a type must declare "Feature Layer" (MapServer roots can
// contain raster/annotation layers esri-leaflet cannot query as features; layers without a type —
// older servers, FeatureServer roots — are kept). supportsQuery is null when the document declares
// no capabilities string, false when capabilities exist without "Query" (the layer list still
// renders, but the map cannot draw those layers). supportsExport is null when the document declares
// no capabilities string, false when capabilities exist without "Map" (image rendering unavailable).
// title is the human-facing service name for seeding the layer Name field: documentInfo.Title
// (MapServer roots), falling back to mapName; null when neither is present (FeatureServer roots
// carry neither).
export function classifyArcgisResponse(json) {
	if (json == null || typeof json !== "object" || Array.isArray(json)) {
		return { kind: "unrecognized" };
	}
	if (json.error != null) {
		return { kind: "arcgisError", message: typeof json.error.message === "string" ? json.error.message : "" };
	}
	if (Array.isArray(json.layers)) {
		let supportsQuery = null;
		let supportsExport = null;
		if (typeof json.capabilities === "string") {
			supportsQuery = json.capabilities.split(",").some((token) => token.trim().toLowerCase() === "query");
			supportsExport = json.capabilities.split(",").some((token) => token.trim().toLowerCase() === "map");
		}
		const layers = json.layers.filter((layer) => {
			if (layer == null) {
				return false;
			}
			if (Array.isArray(layer.subLayerIds) && layer.subLayerIds.length > 0) {
				return false;
			}
			return layer.type == null || layer.type === "Feature Layer";
		});
		let title = null;
		if (json.documentInfo != null && typeof json.documentInfo.Title === "string" && json.documentInfo.Title.trim() !== "") {
			title = json.documentInfo.Title.trim();
		} else if (typeof json.mapName === "string" && json.mapName.trim() !== "") {
			title = json.mapName.trim();
		}
		return { kind: "serviceRoot", layers: layers, supportsQuery: supportsQuery, supportsExport: supportsExport, title: title };
	}
	if (Array.isArray(json.services) || Array.isArray(json.folders)) {
		return { kind: "directory" };
	}
	if (typeof json.id === "number" && (json.geometryType != null || json.type != null)) {
		return {
			kind: "singleLayer",
			id: json.id,
			name: typeof json.name === "string" ? json.name : "",
			geometryType: json.geometryType != null ? json.geometryType : null
		};
	}
	return { kind: "unrecognized" };
}

// Parses the MapLayer.Parameters JSON bag (layer.parameters — absent for legacy rows) into the
// knobs the render path consumes. Never throws: null/blank/invalid JSON, non-objects, unknown
// renderMode values, and non-boolean hideLegend all fall back to the defaults
// (features, no filter, legend shown), so a bad bag can't break a layer.
export function parseMapLayerParameters(json) {
	const defaults = { renderMode: "features", filter: null, hideLegend: false };
	if (typeof json !== "string" || json.trim() === "") {
		return defaults;
	}
	let parsed = null;
	try {
		parsed = JSON.parse(json);
	} catch (error) {
		return defaults;
	}
	if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
		return defaults;
	}
	const filter = typeof parsed.filter === "string" && parsed.filter.trim() !== "" ? parsed.filter : null;
	return { renderMode: parsed.renderMode === "image" ? "image" : "features", filter: filter, hideLegend: parsed.hideLegend === true };
}

// The REST legend resource for a service root. MapServer roots expose {base}/legend; a
// FeatureServer on ArcGIS Server is an extension of the same service, so the sibling
// {…/MapServer}/legend serves the identical sublayer ids. Null for anything else (hosted
// FeatureServers have no sibling — the fetch layer treats the resulting failure as
// legend-unavailable).
export function arcgisLegendUrl(base) {
	if (typeof base !== "string") {
		return null;
	}
	if (/\/MapServer$/i.test(base)) {
		return base + "/legend";
	}
	if (/\/FeatureServer$/i.test(base)) {
		return base.replace(/\/FeatureServer$/i, "/MapServer") + "/legend";
	}
	return null;
}

// Shapes a /legend?f=json document to the selected sublayer ids. [] means "no legend available"
// (malformed/error documents, nothing selected, or no selected layer has usable entries). Items
// without imageData are dropped; blank labels are kept (single-symbol layers have none).
export function filterLegendToLayers(legendJson, selectedIds) {
	if (legendJson == null || typeof legendJson !== "object" || !Array.isArray(legendJson.layers) || !Array.isArray(selectedIds)) {
		return [];
	}
	const wanted = new Set(selectedIds.map((id) => Number(id)));
	const sections = [];
	for (const layer of legendJson.layers) {
		if (layer == null || typeof layer.layerId !== "number" || !wanted.has(layer.layerId) || !Array.isArray(layer.legend)) {
			continue;
		}
		const items = layer.legend
			.filter((item) => item != null && typeof item.imageData === "string" && item.imageData !== "")
			.map((item) => ({
				label: typeof item.label === "string" ? item.label : "",
				imageData: item.imageData,
				contentType: typeof item.contentType === "string" && item.contentType !== "" ? item.contentType : "image/png",
				height: typeof item.height === "number" ? item.height : null,
				width: typeof item.width === "number" ? item.width : null
			}));
		if (items.length > 0) {
			sections.push({
				layerId: layer.layerId,
				layerName: typeof layer.layerName === "string" ? layer.layerName : "",
				items: items
			});
		}
	}
	return sections;
}

// "MapServer" | "FeatureServer" | null for a cleaned base path (arcgisUrlShape().base).
export function arcgisServiceKind(base) {
	if (typeof base !== "string") {
		return null;
	}
	if (/\/MapServer$/i.test(base)) {
		return "MapServer";
	}
	if (/\/FeatureServer$/i.test(base)) {
		return "FeatureServer";
	}
	return null;
}

// The picker's render-mode radio state from the classified service metadata. Image rendering
// (REST export) exists only on MapServer services exposing the Map capability; Features requires
// Query. Image is the default whenever available (Map Viewer parity, exact server cartography,
// bounded payload). When a service supports neither (degenerate), Features stays enabled so the
// radios can never both be disabled.
export function resolveRenderModeOptions(urlKind, supportsQuery, supportsExport) {
	const imageEnabled = urlKind === "MapServer" && supportsExport !== false;
	let featuresEnabled = supportsQuery !== false;
	if (!featuresEnabled && !imageEnabled) {
		featuresEnabled = true;
	}
	return {
		defaultMode: imageEnabled ? "image" : "features",
		featuresEnabled: featuresEnabled,
		imageEnabled: imageEnabled,
	};
}

// Esri's 96-dpi Web Mercator scale denominator at Leaflet zoom 0; each zoom level halves it
// (z13 = 72223.819286). Authored minScale/maxScale values are usually exactly these canonical
// denominators, so the conversions below carry a small epsilon to keep float noise from bumping
// an exact level to its neighbor.
const ZOOM0_SCALE = 591657527.591555;
const SCALE_EPSILON = 1e-6;

function clampZoom(zoom) {
	return Math.min(Math.max(zoom, 0), 18);
}

// Converts a sublayer's authored visible-scale range to the Leaflet zoom window it draws in.
// Esri semantics: the layer draws while maxScale <= mapScale <= minScale; 0 means unlimited on
// that side (null here). Rounding is conservative — ceil for the minimum, floor for the
// maximum — so the seeded window never claims a zoom the service won't draw at. A band narrower
// than one zoom step can hold no integer zoom, which would invert the window (minZoom > maxZoom,
// a layer visible nowhere); it collapses to the single zoom nearest the band instead.
export function scaleRangeToZoomRange(minScale, maxScale) {
	const range = { minZoom: null, maxZoom: null };
	if (Number.isFinite(minScale) && minScale > 0) {
		range.minZoom = clampZoom(Math.ceil(Math.log2(ZOOM0_SCALE / minScale) - SCALE_EPSILON));
	}
	if (Number.isFinite(maxScale) && maxScale > 0) {
		range.maxZoom = clampZoom(Math.floor(Math.log2(ZOOM0_SCALE / maxScale) + SCALE_EPSILON));
	}
	if (range.minZoom != null && range.maxZoom != null && range.minZoom > range.maxZoom) {
		const midpoint = (Math.log2(ZOOM0_SCALE / minScale) + Math.log2(ZOOM0_SCALE / maxScale)) / 2;
		const nearest = clampZoom(Math.round(midpoint));
		range.minZoom = nearest;
		range.maxZoom = nearest;
	}
	return range;
}

// The loosest zoom window covering every selected sublayer (layers = the classified root
// document's raw layers[], layersCsv = the picker's selected-id CSV): the union stays null on a
// side as soon as any selected layer is unlimited there, and both sides are null when nothing
// usable is selected — callers then simply don't seed that field.
export function zoomRangeForSelection(layers, layersCsv) {
	const wanted = new Set(parseSublayerIds(layersCsv));
	let minZoom = null;
	let maxZoom = null;
	let unlimitedMin = false;
	let unlimitedMax = false;
	let anySelected = false;
	if (Array.isArray(layers)) {
		for (const layer of layers) {
			if (layer == null || !wanted.has(Number(layer.id))) {
				continue;
			}
			anySelected = true;
			const range = scaleRangeToZoomRange(layer.minScale, layer.maxScale);
			if (range.minZoom == null) {
				unlimitedMin = true;
			} else if (minZoom == null || range.minZoom < minZoom) {
				minZoom = range.minZoom;
			}
			if (range.maxZoom == null) {
				unlimitedMax = true;
			} else if (maxZoom == null || range.maxZoom > maxZoom) {
				maxZoom = range.maxZoom;
			}
		}
	}
	if (!anySelected) {
		return { minZoom: null, maxZoom: null };
	}
	return { minZoom: unlimitedMin ? null : minZoom, maxZoom: unlimitedMax ? null : maxZoom };
}

// A human-readable default layer name derived from a service URL's name segment —
// ".../rest/services/{folders/}NAME/FeatureServer" -> "NAME" with URL escapes decoded and
// underscores read as spaces ("USA_Wildfires_v1" -> "USA Wildfires v1"). Null when the URL has
// no recognizable service shape. The service's authored title (documentInfo.Title / mapName)
// outranks this; it is the Name-seed fallback for services that publish no title at all —
// hosted FeatureServers commonly don't.
export function serviceNameFromUrl(url) {
	const shape = arcgisUrlShape(typeof url === "string" ? url : "");
	if (shape.base === "" || (!shape.isRoot && shape.sublayerId === null)) {
		return null;
	}
	const match = /\/([^/]+)\/(?:FeatureServer|MapServer)$/i.exec(shape.base);
	if (match == null) {
		return null;
	}
	let name = match[1];
	try {
		name = decodeURIComponent(name);
	} catch (error) {
		// Malformed percent-escapes: keep the raw segment.
	}
	name = name.replace(/_/g, " ").replace(/\s+/g, " ").trim();
	return name === "" ? null : name;
}

// Spatial references extentToLatLngBounds understands: Web Mercator's family of ids, plus
// plain geographic. Anything else resolves null — the preview simply doesn't auto-fit.
const WEB_MERCATOR_WKIDS = new Set([3857, 102100, 102113, 900913]);
// Geographic (decimal-degree) references treated as WGS84 for display: WGS84 itself, NAD83
// (4269 — most US federal servers, within ~1m), and NAD27 (4267 — close enough for a fit).
const GEOGRAPHIC_WKIDS = new Set([4326, 4269, 4267]);
const EARTH_RADIUS = 6378137;

function mercatorToLatLng(x, y) {
	const lng = (x / EARTH_RADIUS) * (180 / Math.PI);
	const lat = (2 * Math.atan(Math.exp(y / EARTH_RADIUS)) - Math.PI / 2) * (180 / Math.PI);
	return [lat, lng];
}

// An ArcGIS extent ({xmin,ymin,xmax,ymax,spatialReference}) as Leaflet fitBounds() input
// [[south,west],[north,east]], or null for missing/degenerate extents and spatial references
// other than WGS84/Web Mercator. Latitudes clamp to Web Mercator's displayable range.
export function extentToLatLngBounds(extent) {
	if (extent == null || typeof extent !== "object") {
		return null;
	}
	if (![extent.xmin, extent.ymin, extent.xmax, extent.ymax].every((value) => Number.isFinite(value))
		|| extent.xmin >= extent.xmax || extent.ymin >= extent.ymax) {
		return null;
	}
	const sr = extent.spatialReference != null && typeof extent.spatialReference === "object" ? extent.spatialReference : {};
	const wkid = Number.isFinite(sr.latestWkid) ? sr.latestWkid : sr.wkid;
	let southWest = null;
	let northEast = null;
	if (GEOGRAPHIC_WKIDS.has(wkid)) {
		southWest = [extent.ymin, extent.xmin];
		northEast = [extent.ymax, extent.xmax];
	} else if (WEB_MERCATOR_WKIDS.has(wkid)) {
		southWest = mercatorToLatLng(extent.xmin, extent.ymin);
		northEast = mercatorToLatLng(extent.xmax, extent.ymax);
	} else {
		return null;
	}
	const clampLat = (lat) => Math.min(Math.max(lat, -85.0511), 85.0511);
	const clampLng = (lng) => Math.min(Math.max(lng, -180), 180);
	return [
		[clampLat(southWest[0]), clampLng(southWest[1])],
		[clampLat(northEast[0]), clampLng(northEast[1])]
	];
}

// Longitude spans at/above this are the antimeridian-wrap signature: a planar ArcGIS extent
// cannot cross ±180, so a dataset with features on both sides (e.g. the Aleutians plus Pacific
// territories in USDA census services) degenerates to the full world width — useless for a fit.
const DEGENERATE_LNG_SPAN = 350;

function fitableBounds(extent) {
	const bounds = extentToLatLngBounds(extent);
	if (bounds == null) {
		return null;
	}
	return bounds[1][1] - bounds[0][1] >= DEGENERATE_LNG_SPAN ? null : bounds;
}

// The preview-fit bounds for a service root document. fullExtent — the data envelope, which
// live services (USA Wildfires) keep tight around current features — wins when usable; the
// author's initialExtent is the rescue for services whose fullExtent is antimeridian-wrapped
// (USDA census: Aleutians + Pacific territories degrade it to the whole world) or otherwise
// unusable. Either is rejected when missing, in an unsupported spatial reference, degenerate,
// or near-world-wide. Null means no metadata fit: the caller leaves the feature-bounds
// fallback fit unclaimed.
export function chooseServiceFitBounds(metadata) {
	if (metadata == null || typeof metadata !== "object") {
		return null;
	}
	const full = fitableBounds(metadata.fullExtent);
	if (full != null) {
		return full;
	}
	return fitableBounds(metadata.initialExtent);
}
